// User management — Supabase implementation.
//
// Reads come straight from `profiles` (RLS already limits a cashier to their
// own row). Writes go through the `admin-users` edge function, because
// creating an auth user, setting a password or disabling an account requires
// the service role — which must never reach the browser.

import { supabase } from '@/lib/supabase';
import type { UserRole } from '@/types/domain';

export interface AppUserWithMeta {
  uid: string;
  email: string;
  displayName: string;
  /** Legacy two-value role. Kept in step with roleId by a database trigger. */
  role: UserRole;
  /** Role id from `roles` — what the permission matrix is keyed on. */
  roleId: string;
  rayonId: string | null;
  isActive: boolean;
  createdAt: number;
  updatedAt?: number;
}

export async function listUsers(): Promise<AppUserWithMeta[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    uid: r.id,
    email: r.email,
    displayName: r.display_name,
    role: r.role,
    roleId: r.role_id,
    rayonId: r.rayon_id,
    isActive: r.is_active,
    createdAt: new Date(r.created_at).getTime(),
    updatedAt: new Date(r.updated_at).getTime(),
  }));
}

export async function getUser(uid: string): Promise<AppUserWithMeta | null> {
  const { data, error } = await supabase
    .from('profiles').select('*').eq('id', uid).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  return {
    uid: data.id,
    email: data.email,
    displayName: data.display_name,
    role: data.role,
    roleId: data.role_id,
    rayonId: data.rayon_id,
    isActive: data.is_active,
    createdAt: new Date(data.created_at).getTime(),
    updatedAt: new Date(data.updated_at).getTime(),
  };
}

/** Thrown when the browser's session is gone or too old to refresh. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Sesi kamu sudah berakhir. Keluar lalu masuk lagi, kemudian ulangi.');
    this.name = 'SessionExpiredError';
  }
}

async function callAdmin<T>(action: string, payload: Record<string, unknown>): Promise<T> {
  // Access tokens live one hour on this deployment (GOTRUE_JWT_EXP=3600). A tab
  // left open overnight wakes with an expired one, and functions.invoke would
  // send it anyway — the edge function then rejects it as Unauthorized and the
  // user sees a bare 401 with no idea what to do.
  //
  // getSession() refreshes a stale token before we use it, so the common case
  // repairs itself silently. Only a session that cannot be refreshed at all
  // reaches the user, and then it says what to do about it.
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new SessionExpiredError();

  const { data, error } = await supabase.functions.invoke<T>('admin-users', {
    body: { action, ...payload },
  });
  if (error) {
    const res = (error as unknown as { context?: Response }).context;
    // 401 here means the token was refused even after the refresh above —
    // treat it as a dead session rather than passing "Unauthorized" through.
    if (res?.status === 401) throw new SessionExpiredError();

    // Edge functions surface the real reason in the response body; the
    // FunctionsHttpError message alone is just "non-2xx status code".
    let detail = '';
    try {
      const text = await res?.text?.();
      if (text) {
        try {
          detail = (JSON.parse(text) as { error?: string }).error ?? text;
        } catch {
          detail = text;
        }
      }
    } catch {
      /* body already consumed or unreadable — fall back to error.message */
    }
    throw new Error(detail || error.message);
  }
  return data as T;
}

export interface CreateUserInput {
  email: string;
  displayName: string;
  role: UserRole;
  password: string;
}

export async function createUserCall(
  input: CreateUserInput,
): Promise<{ uid: string }> {
  return callAdmin<{ uid: string }>('create', { ...input });
}

export async function setUserRoleCall(uid: string, role: UserRole): Promise<void> {
  await callAdmin('set_role', { uid, role });
}

export async function setUserActiveCall(uid: string, isActive: boolean): Promise<void> {
  await callAdmin('set_active', { uid, isActive });
}

/**
 * Issue a password-recovery link for one account.
 *
 * Returns the link rather than mailing it: this deployment has no working SMTP
 * (GOTRUE_SMTP_HOST is "smtp.invalid"), so a send button would report success
 * and deliver nothing. The owner copies the link and sends it through whatever
 * they already use to reach that person.
 *
 * Treat the result as a credential. Whoever holds it can set that account's
 * password until it expires, so it is never stored and never logged.
 */
export async function requestPasswordResetLink(email: string): Promise<string> {
  const { link } = await callAdmin<{ link: string }>('reset_link', { email });
  return link;
}

/** Owner sets a user's password directly. Server refuses < 8 chars. */
export async function setUserPasswordCall(uid: string, password: string): Promise<void> {
  await callAdmin('set_password', { uid, password });
}
